import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { 
  Upload as UploadIcon,
  Science as ScienceIcon 
} from '@mui/icons-material';
import { ColabfoldParams } from '@/types/colabfold';

// Import test sequences from the dedicated file
import { COLABFOLD_TEST_SEQUENCES } from './testParameters';

interface ColabfoldFormProps {
  params: ColabfoldParams;
  onChange: (params: ColabfoldParams) => void;
}

// Helper function to parse FASTA sequences
const parseSequences = (input: string): string[] => {
  if (!input || input.trim() === '') {
    return [];
  }
  
  const sequences: string[] = [];
  
  // Handle FASTA format with headers
  if (input.includes('>')) {
    const lines = input.split('\n');
    let currentSequence = '';
    let currentHeader = '';
    
    for (const line of lines) {
      if (line.startsWith('>')) {
        // If we already have a sequence, push it
        if (currentHeader && currentSequence) {
          sequences.push(`${currentHeader}\n${currentSequence}`);
          currentSequence = '';
        }
        currentHeader = line;
      } else if (line.trim() && currentHeader) {
        // Add to current sequence if not empty
        currentSequence += line.trim();
      }
    }
    
    // Add the last sequence
    if (currentHeader && currentSequence) {
      sequences.push(`${currentHeader}\n${currentSequence}`);
    }
  } else {
    // Single sequence without header
    if (input.trim()) {
      sequences.push(input.trim());
    }
  }
  
  return sequences;
};

const ColabfoldForm: React.FC<ColabfoldFormProps> = ({ params, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sequenceCount, setSequenceCount] = useState<number>(0);
  
  // Update sequence count when query_sequence changes
  useEffect(() => {
    const sequences = parseSequences(params.query_sequence);
    setSequenceCount(sequences.length);
  }, [params.query_sequence]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setSelectedFile(file.name);
        const text = await file.text();
        if (text.trim().length === 0) {
          throw new Error('File is empty');
        }
        onChange({
          ...params,
          query_sequence: text
        });
        setError(null);
      } catch (err) {
        setError(`Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setSelectedFile(null);
      }
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box mb={3}>
        <Typography variant="subtitle2" gutterBottom>
          Test Sequences
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Load Test Sequence 1">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ScienceIcon />}
              onClick={() => {
                onChange({
                  ...params,
                  query_sequence: COLABFOLD_TEST_SEQUENCES.test1.sequence,
                  ...COLABFOLD_TEST_SEQUENCES.test1.params
                });
              }}
            >
              Test 1
            </Button>
          </Tooltip>
          <Tooltip title="Load Test Sequence 2">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ScienceIcon />}
              onClick={() => {
                onChange({
                  ...params,
                  query_sequence: COLABFOLD_TEST_SEQUENCES.test2.sequence,
                  ...COLABFOLD_TEST_SEQUENCES.test2.params
                });
              }}
            >
              Test 2
            </Button>
          </Tooltip>
          <Tooltip title="Load Multiple Test Sequences (Batch)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ScienceIcon />}
              onClick={() => {
                const batchSequence = `>Test_Sequence_1
MAAATLRLLLAAGVSILVGAGFANQTEVSNDLISRTWQTISKLEAAIKDELKKGDTKELKNTLLKAYEEGDKIFVLDLTR
>Test_Sequence_2
MKLKPAIEIVSLFVTCLAAVLNAFPGAASSDFETLTGRRTTNISKLEAAIKDGLKKGDTKELKNTLLKAYEEGDKIFVLEVRR`;
                onChange({
                  ...params,
                  query_sequence: batchSequence
                });
              }}
            >
              Test Batch
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        fullWidth
        label="Sequence Input"
        multiline
        rows={8}
        value={params.query_sequence}
        onChange={(e) => onChange({...params, query_sequence: e.target.value})}
        placeholder="Enter your sequence(s) here or upload a FASTA file. Multiple sequences will be processed as a batch job."
        required
        sx={{ mb: 2 }}
      />
      
      {sequenceCount > 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {sequenceCount} sequences detected. These will be processed as separate jobs in a batch.
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ mb: 1 }}
        >
          Upload FASTA File
        </Button>
        {selectedFile && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            Selected file: {selectedFile}
          </Typography>
        )}
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".fasta,.fa,.txt"
        style={{ display: 'none' }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="template-mode-label">Template Mode</InputLabel>
        <Select
          labelId="template-mode-label"
          value={params.template_mode}
          label="Template Mode"
          onChange={(e) => onChange({
            ...params,
            template_mode: e.target.value as 'none' | 'pdb100' | 'custom'
          })}
        >
          <MenuItem value="none">None (No templates)</MenuItem>
          <MenuItem value="pdb100">PDB100 (All templates)</MenuItem>
          <MenuItem value="custom">Custom (Upload your own templates)</MenuItem>
        </Select>
        <FormHelperText>
          Templates can improve prediction accuracy for proteins with known structures
        </FormHelperText>
      </FormControl>

      <TextField
        select
        fullWidth
        label="Number of Relaxations"
        value={params.num_relax}
        onChange={e => onChange({
          ...params,
          num_relax: Number(e.target.value)
        })}
        SelectProps={{
          native: true,
        }}
        sx={{ mb: 2 }}
      >
        <option value={0}>0 (No relaxation)</option>
        <option value={1}>1 (Relax top model)</option>
        <option value={5}>5 (Relax all models)</option>
      </TextField>
    </Box>
  );
};

export default ColabfoldForm; 